import { useMediaQuery, useTheme } from '@mui/material';
import type {
  GridColumnVisibilityModel,
  GridRowClassNameParams,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { ScheduledShowDialog } from '~/components/schedule/dialog';
import type { ScheduleInput } from '~/generated/graphql';
import { useOrderByParams } from '~/hooks/order-by';
import AddScheduleIllustration from '~/images/illustrations/add-schedule.svg';
import { isExpired } from '~/lib/schedules';
import type { DeviceSchedule__ScheduledShow as ScheduledShow } from '../../../../queries/schedule.generated';
import { useColumns } from '../lib/columns';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={AddScheduleIllustration}
      header={t('pages.devices.show.schedule.empty_state_header')}
      description={t('pages.devices.show.schedule.empty_state_description')}
    />
  );
};

export interface TableProps {
  currentScheduledShowId?: number;
  loading: boolean;
  schedules: readonly ScheduledShow[];
  onDelete: (scheduledShow: ScheduledShow) => void;
  onRemoveDevice: (scheduledShow: ScheduledShow) => void;
  onSubmit: (
    name: string,
    schedule: ScheduleInput,
    showId: number,
    scheduledShowId?: number,
  ) => Promise<void>;
}

export const Table = ({
  currentScheduledShowId,
  loading,
  onDelete,
  onRemoveDevice,
  onSubmit,
  schedules,
}: TableProps) => {
  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { setOrderByParams, sortModel } = useOrderByParams();

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setOrderByParams(sortModel.at(0) ?? null);
    },
    [setOrderByParams],
  );

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      devices: !isSmallAndDown,
      label: !isSmallAndDown,
      schedule: !isSmallAndDown,
      start: !isSmallAndDown,
    }));
  }, [isSmallAndDown]);

  const [currentSchedule, setCurrentSchedule] = useState<ScheduledShow>();
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const onUpdate = (scheduledShow: ScheduledShow) => {
    setCurrentSchedule(scheduledShow);
    setEditDialogOpen(true);
  };

  const columns = useColumns({
    currentScheduledShowId,
    onDelete,
    onRemoveDevice,
    onUpdate,
  });

  return (
    <>
      <ScrollableDataGrid
        checkboxSelection={false}
        columns={columns}
        columnVisibilityModel={showColumns}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        disableRowSelectionOnClick
        loading={loading}
        onColumnVisibilityModelChange={setShowColumns}
        getRowClassName={({ row }: GridRowClassNameParams<ScheduledShow>) =>
          isExpired(row.schedule?.rrule) ? `expired` : 'readonly'
        }
        rows={schedules}
        slots={{
          columnResizeIcon: () => null,
          noRowsOverlay: NoRowsOverlay,
        }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        sx={{
          '& .expired:hover': {
            cursor: 'default',
            backgroundColor: 'inherit',
          },
        }}
      />
      {currentSchedule && (
        <ScheduledShowDialog
          onClose={() => setEditDialogOpen(false)}
          onSubmit={onSubmit}
          open={editDialogOpen}
          scheduledShow={currentSchedule}
        />
      )}
    </>
  );
};
